<template>
    <div class="policy-wrapper p-4 md:p-6 surface-card border-round-xl shadow-1 text-left">
        <div class="mb-5 border-bottom-1 border-300 pb-3">
            <h1 class="H4-SemiBold text-900 mb-3">USER AGREEMENT</h1>
            <p class="text-900 mb-2">Version: 1.0</p>
            <p class="text-900 mb-2">Jurisdiction: Democratic Socialist Republic of Sri Lanka</p>
            <p class="text-600 italic font-light">
                These Terms take effect from the date you access, browse, register an account on, or otherwise use the Platform or its services.
            </p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">1. INTRODUCTION</h2>
            <p class="text-body mb-2">
                1.1 This User Agreement (“Agreement”) governs access to and use of the ILearn’d Tutors website, mobile application, and related services (collectively, the “Platform”).
            </p>
            <p class="text-body mb-2">
                1.2 By accessing, registering, browsing, or using the Platform in any capacity, you acknowledge that you have read, understood, and agree to be legally bound by this Agreement together with:
            </p>
            <ul class="text-body list-none p-0 m-0 ml-3 mb-2">
                <li>• applicable Terms and Conditions (General Users, Students, Tutors, or Institutes, as relevant);</li>
                <li>• Privacy Policy;</li>
                <li>• Child Protection & Safeguarding Policy;</li>
                <li>• Cookie Policy; and</li>
                <li>• any other policies or guidelines published by ILearn’d Tutors from time to time.</li>
            </ul>
            <p class="text-body">
                1.3 If you do not agree to this Agreement, you must immediately discontinue use of the Platform.
            </p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">2. NATURE OF THE PLATFORM</h2>
            <p class="text-body mb-2">2.1 ILearn’d Tutors is an <b>educational technology platform</b> that facilitates:</p>
            <ul class="text-body list-none p-0 m-0 ml-3 mb-3">
                <li>(a) connections between students, tutors, and institutes;</li>
                <li>(b) scheduling, communication, and secure payment processing;</li>
                <li>(c) access to educational content, past papers, and learning tools; and</li>
                <li>(d) analytics and administrative support features.</li>
            </ul>
            <p class="text-body mb-2">2.2 ILearn’d Tutors does <b>not:</b></p>
            <ul class="text-body list-none p-0 m-0 ml-3 mb-3">
                <li>(a) employ tutors or instructors;</li>
                <li>(b) control academic content, curriculum, or teaching methods; or</li>
                <li>(c) guarantee academic performance, examination results, or learning outcomes.</li>
            </ul>
            <p class="text-body">
                2.3 Users engage with the Platform at their own discretion and remain responsible for their educational decisions and interactions.
            </p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">3. ELIGIBILITY</h2>
            <p class="text-body mb-2">3.1 To use the Platform, you must:</p>
            <ul class="text-body list-none p-0 m-0 ml-3 mb-2">
                <li>(a) be at least <b>thirteen (13) years of age;</b></li>
                <li>(b) obtain <b>verifiable parental or legal guardian consent</b> if under eighteen (18);</li>
                <li>(c) provide accurate and complete information during registration; and</li>
                <li>(d) use the Platform solely for lawful purposes.</li>
            </ul>
            <p class="text-body">
                3.2 ILearn’d Tutors reserves the right to restrict, suspend, or terminate access where eligibility requirements are not met.
            </p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">4. USER ACCOUNT RESPONSIBILITIES</h2>
            <p class="text-body mb-2">4.1 When creating an account, you agree to:</p>
            <ul class="text-body list-none p-0 m-0 ml-3 mb-2">
                <li>(a) provide accurate, current, and complete personal information;</li>
                <li>(b) maintain the confidentiality of login credentials;</li>
                <li>(c) notify ILearn’d Tutors immediately of any unauthorised access or security breach; and</li>
                <li>(d) refrain from sharing accounts with third parties.</li>
            </ul>
            <p class="text-body">4.2 You are solely responsible for all activities conducted under your account.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">5. PERMITTED USE OF THE PLATFORM</h2>
            <p class="text-body mb-2">5.1 Users may use the Platform to:</p>
            <ul class="text-body list-none p-0 m-0 ml-3">
                <li>(a) browse tutor and institute information;</li>
                <li>(b) book and attend tutoring sessions;</li>
                <li>(c) communicate through Platform-approved tools;</li>
                <li>(d) access academic content, including past papers (subject to access level); and</li>
                <li>(e) manage learning or teaching activities.</li>
            </ul>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">6. PROHIBITED ACTIVITIES</h2>
            <p class="text-body mb-2">6.1 Users shall not:</p>
            <ul class="text-body list-none p-0 m-0 ml-3 mb-3">
                <li>(a) violate any applicable laws of the Democratic Socialist Republic of Sri Lanka;</li>
                <li>(b) interfere with Platform security, integrity, or operations;</li>
                <li>(c) create false or misleading profiles;</li>
                <li>(d) introduce malware, bots, scraping tools, or hacking attempts;</li>
                <li>(e) harass, abuse, threaten, or impersonate other users;</li>
                <li>(f) engage in off-platform payments or arrangements intended to bypass Platform fees;</li>
                <li>(g) share personal contact details with minors outside the Platform;</li>
                <li>(h) copy, sell, reproduce, or distribute Platform content without authorisation;</li>
                <li>(i) collect or misuse personal data of other users;</li>
                <li>(j) post false, defamatory, harmful, or misleading content; or</li>
                <li>(k) record sessions without the express consent of all parties.</li>
            </ul>
            <p class="text-body">6.2 Any violation may result in suspension, termination, or legal action.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">7. MODE OF SESSIONS (ONLINE-ONLY RULE)</h2>
            <p class="text-body mb-2">7.1 All tutoring sessions facilitated through the Platform shall be conducted <b>exclusively online</b> using Platform-approved tools.</p>
            <p class="text-body mb-2">7.2 In-person sessions at a student’s home, tutor’s residence, or other physical locations are <b>strictly prohibited.</b></p>
            <p class="text-body">7.3 ILearn’d Tutors assumes no responsibility or liability for any sessions conducted outside the Platform.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">8. PAYMENTS, CANCELLATIONS & REFUNDS</h2>
            <p class="text-body mb-2">8.1 All payments must be processed <b>exclusively through the Platform’s authorised payment systems.</b></p>
            <p class="text-body mb-2">8.2 By booking a session, users authorise ILearn’d Tutors to <b>automatically charge the applicable session fee only upon successful completion of the session.</b></p>
            <p class="text-body mb-2">8.3 Cancellation rules, charge decisions, and refunds (where applicable) shall be governed by the relevant Student, Tutor, or Institute Terms and the <b>Refund & Dispute Resolution Policy.</b></p>
            <p class="text-body">8.4 Off-platform payments are strictly prohibited and may result in account termination.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">9. CHILD PROTECTION & SAFETY</h2>
            <p class="text-body mb-2">9.1 ILearn’d Tutors enforces a <b>zero-tolerance policy</b> towards child abuse, exploitation, grooming, or inappropriate conduct involving minors.</p>
            <p class="text-body mb-2">9.2 All users must comply with the Child Protection & Safeguarding Policy.</p>
            <p class="text-body">9.3 ILearn’d Tutors may report suspected abuse or criminal activity to the Sri Lanka Police, the National Child Protection Authority (NCPA), or other competent authorities and may share relevant data where legally required.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">10. CONTENT & INTELLECTUAL PROPERTY</h2>
            <p class="text-body mb-2">10.1 All Platform content, including software, design, branding, academic materials, and past papers, is owned by ILearn’d Tutors or its licensors.</p>
            <p class="text-body mb-2">10.2 Content may be viewed and used <b>solely for personal, non-commercial purposes.</b></p>
            <p class="text-body">10.3 No content may be copied, reproduced, distributed, or commercialised without prior written permission.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">11. USER-GENERATED CONTENT</h2>
            <p class="text-body mb-2">11.1 Users may submit reviews, messages, feedback, or other content.</p>
            <p class="text-body mb-2">11.2 By submitting such content, you grant ILearn’d Tutors a <b>non-exclusive, royalty-free, worldwide licence</b> to use, display, and reproduce the content for Platform-related purposes.</p>
            <p class="text-body">11.3 ILearn’d Tutors reserves the right to moderate or remove content at its discretion.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">12. AI FEATURES</h2>
            <p class="text-body mb-2">12.1 The Platform may provide <b>AI-enabled features</b> to support learning, analytics, summaries, or administrative tasks.</p>
            <p class="text-body mb-2">12.2 AI features are <b>assistive only</b> and do not replace tutors, curricula, or examination authority guidance.</p>
            <p class="text-body">12.3 ILearn’d Tutors does not guarantee the accuracy or outcomes of AI-generated outputs.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">13. DATA PROTECTION & PRIVACY</h2>
            <p class="text-body mb-2">13.1 Personal data is processed in accordance with the <b>Personal Data Protection Act, No. 9 of 2022 of Sri Lanka (PDPA)</b> and the Privacy Policy.</p>
            <p class="text-body">13.2 Data may be disclosed to authorities where legally required.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">14. PLATFORM CHAT & MESSAGING TERMS</h2>
            
            <p class="text-body font-bold mb-1">14.1 Platform-Only Communication</p>
            <p class="text-body mb-2">
                14.1.1 All communication between users of the ILearn’d Tutors Platform, including but not limited to students, tutors, parents or legal guardians, and institutes, <b>must take place exclusively through the Platform’s in-built chat and messaging system.</b>
            </p>
            <p class="text-body mb-2">14.1.2 The Platform chat system is provided to support:</p>
            <ul class="text-body list-none p-0 m-0 ml-3 mb-2">
                <li>(a) educational communication;</li>
                <li>(b) lesson coordination and scheduling;</li>
                <li>(c) safeguarding and child protection;</li>
                <li>(d) record-keeping and dispute resolution; and</li>
                <li>(e) Platform compliance, safety, and security.</li>
            </ul>
            <p class="text-body mb-3">
                14.1.3 <b>Off-platform communication</b>, including but not limited to WhatsApp, Telegram, SMS, phone calls, email, or social media platforms, is <b>strictly prohibited</b>, particularly where minors are involved.
            </p>

            <p class="text-body font-bold mb-1">14.2 Acceptable Use of Chat</p>
            <p class="text-body mb-1">14.2.1 The chat system shall be used <b>solely for educational, administrative, and Platform-related purposes.</b></p>
            <p class="text-body mb-3">14.2.2 All communication must remain: (a) professional; (b) respectful; and (c) appropriate to the educational context.</p>

            <p class="text-body font-bold mb-1">14.3 Prohibited Conduct in Chat</p>
            <p class="text-body mb-1">14.3.1 Users shall not, through the Platform chat system:</p>
            <ul class="text-body list-none p-0 m-0 ml-3 mb-2">
                <li>(a) share personal contact details, including phone numbers, email addresses, or social media handles;</li>
                <li>(b) attempt to move conversations off the Platform;</li>
                <li>(c) solicit private tutoring, payments, or arrangements outside the Platform;</li>
                <li>(d) engage in harassment, bullying, intimidation, or abusive language;</li>
                <li>(e) send inappropriate, sexual, suggestive, or offensive content;</li>
                <li>(f) share copyrighted material without authorisation;</li>
                <li>(g) misuse personal data or confidential information; or</li>
                <li>(h) encourage secrecy, unsupervised interaction, or circumvention of Platform rules.</li>
            </ul>
            <p class="text-body mb-3">
                14.3.2 Any attempt to use the chat system to bypass Platform safeguards, payments, subscriptions, or monitoring <b>constitutes a material breach</b> of the applicable Terms.
            </p>

            <p class="text-body font-bold mb-1">14.4 Child Protection & Safeguarding</p>
            <p class="text-body mb-1">14.4.1 Where a student is a minor:</p>
            <ul class="text-body list-none p-0 m-0 ml-3 mb-2">
                <li>(a) all chat communication must remain strictly educational;</li>
                <li>(b) private or personal discussions are prohibited; and</li>
                <li>(c) grooming behaviour, boundary violations, or emotional manipulation are strictly forbidden.</li>
            </ul>
            <p class="text-body mb-3">14.4.2 ILearn’d Tutors enforces a <b>zero-tolerance policy</b> for child protection violations occurring through the chat system.</p>

            <p class="text-body font-bold mb-1">14.5 Monitoring, Retention & Data Protection</p>
            <p class="text-body mb-2">14.5.1 ILearn’d Tutors may: (a) log, store, and review chat communications; (b) use automated and manual moderation tools; and (c) review chat records for safety, compliance, fraud prevention, and dispute resolution.</p>
            <p class="text-body mb-2">14.5.2 Chat records may be retained: (a) for up to ninety (90) days for operational purposes; or (b) longer where required for investigations, safeguarding, disputes, or legal compliance.</p>
            <p class="text-body mb-3">14.5.3 All monitoring and data processing is conducted in accordance with the <b>Personal Data Protection Act, No. 9 of 2022 (Sri Lanka)</b> and the Platform’s Privacy Policy.</p>

            <p class="text-body font-bold mb-1">14.6 AI-Assisted Moderation</p>
            <p class="text-body mb-2">14.6.1 The Platform may utilise AI-enabled tools to: (a) detect inappropriate language or safeguarding risks; (b) flag potential policy violations; and (c) assist with moderation and safety alerts.</p>
            <p class="text-body mb-3">14.6.2 AI systems are <b>assistive only</b> and do not replace human review or decision-making.</p>

            <p class="text-body font-bold mb-1">14.7 Reporting & Enforcement</p>
            <p class="text-body mb-2">14.7.1 Users may report chat misuse through: (a) in-chat reporting tools; (b) the Platform’s complaints or contact form; or (c) email to <b>support@ilearndsl.com.</b></p>
            <p class="text-body mb-3">14.7.2 Violations of this Clause may result in: (a) warnings; (b) chat restrictions; (c) temporary suspension; (d) permanent account termination; (e) loss of access without refund; and (f) reporting to relevant authorities where required by law.</p>

            <p class="text-body">
                14.8 <b>No Liability for Off-Platform Communication</b><br />
                14.8.1 ILearn’d Tutors shall not be responsible or liable for any communication, interaction, or dispute arising from conversations conducted outside the Platform.
            </p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">15. COMPLAINTS & DISPUTE RESOLUTION</h2>
            <p class="text-body mb-2">
                15.1 Complaints should be submitted <b>as soon as reasonably practicable,</b> and ordinarily within <b>forty-eight (48) hours,</b> unless circumstances beyond the user’s control prevent timely submission.
            </p>
            <p class="text-body">15.2 Complaints shall be handled in accordance with the <b>Formal Complaints Procedure.</b></p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">16. SUSPENSION & TERMINATION</h2>
            <p class="text-body mb-2">16.1 ILearn’d Tutors may suspend or terminate access for policy violations, safety concerns, fraud, abuse, off-platform payments, or child protection issues.</p>
            <p class="text-body">16.2 Users may close their accounts at any time subject to settlement of outstanding obligations.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">17. LIMITATION OF LIABILITY</h2>
            <p class="text-body mb-2">17.1 The Platform is provided on an <b>“as is”</b> basis.</p>
            <p class="text-body mb-2">17.2 ILearn’d Tutors shall not be liable for academic outcomes, user conduct, or off-platform interactions.</p>
            <p class="text-body">17.3 To the maximum extent permitted by law, total liability shall not exceed the fees paid to the Platform in the preceding six (6) months.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">18. GOVERNING LAW & JURISDICTION</h2>
            <p class="text-body mb-2">
                This Agreement shall be governed by and construed in accordance with the laws of the <b>Democratic Socialist Republic of Sri Lanka.</b>
            </p>
            <p class="text-body">
                All disputes shall be subject to the <b>jurisdiction of the courts of Sri Lanka.</b>
            </p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">19. AMENDMENTS</h2>
            <p class="text-body mb-2">19.1 ILearn’d Tutors may update this Agreement from time to time.</p>
            <p class="text-body">
                19.2 Material changes shall be notified through the Platform and, where required by law, shall require <b>explicit consent</b> before taking effect.
            </p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">20. CONTACT INFORMATION</h2>
            <p class="text-body font-bold mb-2">ILearn’d Tutors – Legal Team</p>
            <p class="text-body mb-1">
                Email: <a href="mailto:support@ilearndsl.com" class="text-primary no-underline font-bold">support@ilearndsl.com</a>
            </p>
            <p class="text-body">
                Address: 306/1 Highlevel Road, Colombo 06, Sri Lanka
            </p>
        </div>
    </div>
</template>

<style scoped>
.text-primary { color: #1D97BD !important; }
.text-body {
    font-size: 0.95rem;
    color: #4A5568;
    font-weight: 400;
    line-height: 1.6;
}
.bg-faded { background-color: #f8fafc; }
</style>